import { Box, Button, Grid, InputAdornment, Paper, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import { MaterialTableProps } from 'material-table';
import Moment from 'moment';
import React, { FunctionComponent, useEffect } from 'react';
import { toast } from 'react-toastify';
import ActivityLogDataModel from '../../../data-models/admin/access-control/ActivityLog.DataModel';
import ActivityTypeDataModel from '../../../data-models/admin/access-control/ActivityType.DataModel';
import UserAdminTableDataModel from '../../../data-models/UserAdminTableDataModel';
import ErrorLogEntity from '../../../entities/error-log/ErrorLog.Entity';
import sendErrorLog from '../../../services/ErrorLog.Service';
import { getActivityLog, getActivityLogUsers, getActivityTypes } from '../../../services/ActivityLog.Service';
import { Loader } from '../../generic-components/loader/Loader.Component';
import MaterialTable from '../../generic-components/material-table/MaterialTable.Component';
import styles from './AccessControlActivityLog.Styles';

const DATE_INPUT_FORMAT = 'YYYY-MM-DD';
const DEFAULT_RANGE_IN_DAYS = 30;

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'Unknown error';
};

const isAbortError = (error: unknown): boolean => {
    if (error instanceof Error && error.name === 'AbortError') {
        return true;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }
    return false;
};

export const AccessControlActivityLog: FunctionComponent = () => {
    const classes = styles();
    const abortController = React.useRef(new AbortController());

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [users, setUsers] = React.useState<Array<UserAdminTableDataModel>>([]);
    const [activityTypes, setActivityTypes] = React.useState<Array<ActivityTypeDataModel>>([]);
    const [activities, setActivities] = React.useState<Array<ActivityLogDataModel>>([]);
    const [selectedUser, setSelectedUser] = React.useState<UserAdminTableDataModel>(null);
    const [selectedActivityType, setSelectedActivityType] = React.useState<ActivityTypeDataModel>(null);
    // The log now spans every activity type, so the screen opens on a bounded range instead of the full history.
    const [dateFrom, setDateFrom] = React.useState<string>(() =>
        Moment().subtract(DEFAULT_RANGE_IN_DAYS, 'days').format(DATE_INPUT_FORMAT),
    );
    const [dateTo, setDateTo] = React.useState<string>(() => Moment().format(DATE_INPUT_FORMAT));
    const [truncatedAt, setTruncatedAt] = React.useState<number>(0);

    useEffect(() => {
        const controller = new AbortController();
        abortController.current = controller;
        handleLoadFilters(controller.signal);

        return () => controller.abort();
    }, []);

    const logError = async (context: string, error: unknown) => {
        try {
            const errorLog = new ErrorLogEntity();
            errorLog.ErrorMessage = `${context}: ${getErrorMessage(error)}`;
            await sendErrorLog(errorLog, abortController.current.signal);
        } catch (error_) {
            console.error('Failed to send error log:', error_);
        }
    };

    const handleLoadFilters = async (signal: AbortSignal) => {
        try {
            const [usersResult, activityTypesResult] = await Promise.all([
                getActivityLogUsers(signal),
                getActivityTypes(signal),
            ]);

            setUsers(usersResult ?? []);
            setActivityTypes(activityTypesResult ?? []);
        } catch (error) {
            if (isAbortError(error) || signal.aborted) {
                return;
            }
            await logError('Error loading Activity Log filters', error);
            toast.warning('⚠️ Error loading Activity Log filters.');
        }
    };

    const handleGetActivity = async () => {
        setIsLoading(true);

        try {
            const result = await getActivityLog(
                {
                    userId: selectedUser?.userId,
                    activityTypeId: selectedActivityType?.activityTypeId,
                    dateFrom: dateFrom || undefined,
                    dateTo: dateTo || undefined,
                },
                abortController.current.signal,
            );

            setActivities(result?.activities ?? []);
            setTruncatedAt(result?.isTruncated ? result.maxRows : 0);
        } catch (error) {
            if (!isAbortError(error) && !abortController.current.signal.aborted) {
                await logError('Error retrieving the Activity Log', error);
                toast.warning('⚠️ Error retrieving the Activity Log.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getUserLabel = (user: UserAdminTableDataModel): string => {
        const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        return fullName.length > 0 ? fullName : user.email ?? '';
    };

    const activityTableProps: MaterialTableProps<ActivityLogDataModel> = {
        columns: [
            {
                title: 'User',
                field: 'userName',
            },
            {
                title: 'Activity Type',
                field: 'activityType',
            },
            {
                title: 'Date',
                field: 'date',
                render: (rowData) => (
                    <Box component="span" className={classes.dateCell}>
                        {rowData.date ? Moment(rowData.date).format('MM/DD/YYYY hh:mm A') : ''}
                    </Box>
                ),
            },
            {
                title: 'Description',
                field: 'description',
            },
        ],
        data: activities,
        options: {
            showTitle: true,
            toolbar: true,
            search: true,
            paging: true,
            exportButton: true,
            exportFileName: 'TerraClaimConnect_Admin_ActivityLog',
            pageSizeOptions: [10, 25, 50],
            pageSize: 50,
            paginationPosition: 'top',
            emptyRowsWhenPaging: false,
        },
        style: {
            paddingLeft: '35px',
            paddingRight: '35px',
        },
        title: 'Activity Log',
    };

    return (
        <Paper className={classes.paper}>
            <Grid container spacing={3} className={classes.gridContainer}>
                <Grid item xs={3}>
                    <Autocomplete<UserAdminTableDataModel, false, false, false>
                        id="activity-log-user"
                        options={users}
                        value={selectedUser}
                        getOptionLabel={getUserLabel}
                        getOptionSelected={(option, value) => option.userId === value.userId}
                        onChange={(_event, value) => setSelectedUser(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="User"
                                className={classes.filterInput}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Autocomplete<ActivityTypeDataModel, false, false, false>
                        id="activity-log-activity-type"
                        options={activityTypes}
                        value={selectedActivityType}
                        getOptionLabel={(option) => option.title ?? ''}
                        getOptionSelected={(option, value) => option.activityTypeId === value.activityTypeId}
                        onChange={(_event, value) => setSelectedActivityType(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Activity Type"
                                className={classes.filterInput}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="From"
                        id="activity-log-date-from"
                        type="date"
                        className={classes.filterInput}
                        InputLabelProps={{ shrink: true }}
                        value={dateFrom}
                        onChange={(event) => setDateFrom(event.target.value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="To"
                        id="activity-log-date-to"
                        type="date"
                        className={classes.filterInput}
                        InputLabelProps={{ shrink: true }}
                        value={dateTo}
                        onChange={(event) => setDateTo(event.target.value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Button className={classes.button} onClick={handleGetActivity}>
                        Get Activity
                    </Button>
                </Grid>
            </Grid>
            {truncatedAt > 0 && !isLoading && (
                <Box className={classes.truncationNotice}>
                    {`Only the most recent ${truncatedAt.toLocaleString()} activities are shown. Narrow the filters to see older entries.`}
                </Box>
            )}
            {isLoading ? <Loader isSmall /> : <MaterialTable tableProps={activityTableProps} />}
        </Paper>
    );
};
